/**
 * EventCard.js — Event Card (cardType "event").
 *
 * Renders one event or reserved_event feature card inside CardCarousel: cover
 * image, title, date/time/location meta, sub-title, price, and a public CTA
 * link. Both BE types map to cardType "event" (see chat-handler TYPE_TO_CARD).
 *
 * PARITY: old createEvents used a SINGLE template for both events_information
 * and reserved_events — it had no SOLD OUT badge and no availability /
 * display_button gating, the CTA was always shown when cta_link existed, and
 * the cover image itself opened the detail view. This card mirrors that
 * exactly; the reserved_event-only fields (sold_out, display_button,
 * sub_location) are intentionally not used in the listing view.
 *
 * BE FIELDS (apps/resources/feature_cards/constants.py — event / reserved_event):
 *   title             {string}  required
 *   sub_title         {string}
 *   description        {string}
 *   date              {string}  ISO date e.g. 2026-05-15
 *   time              {string}
 *   location          {string}
 *   sub_location      {string}  (reserved_event)
 *   price             {string}
 *   price_unit        {string}  (reserved_event)
 *   available         {bool}    (not used in listing — see PARITY note)
 *   display_button    {bool}    (reserved_event — not used in listing)
 *   sold_out          {bool}    (reserved_event — not used in listing)
 *   cta_text          {string}
 *   cta_link          {string}
 *   cover_image_link  {string}
 *
 * LEARN MORE:
 *   Mirrors old cardClicked -> sendMessage: emits ws-prompt-select with the
 *   card's `message`, which chat.js echoes as a user turn and sends to the BE
 *   for the detailed_event_information view (same pattern as TastingCard).
 *
 * REFERENCE (old code):
 *   thomasfogarty/script.js createEvents() (2052-2107)
 */

import { emit } from "../../core/state.js";
import { getDefaultCardLogo } from "../../core/defaultCardLogos.js";

const CAL_ICON =
  '<svg viewBox="0 0 13 15" width="13" height="14" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.83 0.5v2.67M9.17 0.5v2.67M0.5 5.83h12M1.83 1.83h9.34A1.33 1.33 0 0 1 12.5 3.17v9.33a1.33 1.33 0 0 1-1.33 1.33H1.83A1.33 1.33 0 0 1 0.5 12.5V3.17a1.33 1.33 0 0 1 1.33-1.34z"/></svg>';

export default class EventCard {
  /**
   * @param {Object} ev - event / reserved_event feature card
   * @returns {HTMLElement}
   */
  render(ev) {
    const card = document.createElement("div");
    card.className = "ws-card ws-event-card";

    // Old createEvents used ONE template for both events_information and
    // reserved_events: no SOLD OUT badge, no availability/display_button gate,
    // CTA always shown, and the cover image itself opens the detail view.
    const openDetail = () => {
      const label = ev.message || `Tell me more about ${ev.title || "this event"}`;
      emit("ws-prompt-select", { label, value: label, uniqueId: ev.unique_id });
    };

    const head = document.createElement("div");
    head.className = "ws-card-head";
    
    const img = document.createElement("img");
    img.className = "ws-card-img";
    img.src = ev.cover_image_link || getDefaultCardLogo();
    img.alt = ev.title || "Event";
    img.loading = "lazy";
    head.appendChild(img);
    // Parity: old .event-cover-image had onclick cardClicked → detail view.
    head.style.cursor = "pointer";
    head.addEventListener("click", openDetail);
    
    card.appendChild(head);

    const body = document.createElement("div");
    body.className = "ws-card-body";

    if (ev.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = ev.title;
      title.textContent = ev.title;
      body.appendChild(title);
    }

    // Old listing rendered date / time / location only (sub_location is a
    // detail-view field; createEvents never merged it into the location line).
    // The --date/--time/--location modifier lets a variant style each line by
    // kind (old .event_date_range/.event_time/.event_location), not position.
    const metaLines = [
      ["date", ev.date],
      ["time", ev.time],
      ["location", ev.location],
    ].filter(([, text]) => text);
    metaLines.forEach(([kind, text]) => {
      const p = document.createElement("p");
      p.className = `ws-card-meta ws-card-meta--${kind}`;
      p.textContent = text;
      body.appendChild(p);
    });

    if (ev.sub_title) {
      const sub = document.createElement("p");
      sub.className = "ws-card-desc";
      sub.textContent = ev.sub_title;
      body.appendChild(sub);
    }

    // Learn more → drives the detailed_event_information follow-up turn.
    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${CAL_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    body.appendChild(learn);

    // Footer: price + unit stacked on the left, CTA on the right — mirrors old
    // .event-footer (a price-details block with two stacked <p> beside the
    // reserve link). Same row layout the tasting card uses.
    const footer = document.createElement("div");
    footer.className = "ws-event-footer";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-price-block";
    if (ev.price) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = ev.price;
      priceBlock.appendChild(price);
    }
    if (ev.price_unit) {
      const unit = document.createElement("p");
      unit.className = "ws-card-unit";
      unit.textContent = ev.price_unit;
      priceBlock.appendChild(unit);
    }
    if (priceBlock.children.length) footer.appendChild(priceBlock);

    // Old always rendered the CTA <a> when cta_link existed — no sold_out /
    // available / display_button gating in the listing view.
    if (ev.cta_link) {
      const cta = document.createElement("a");
      cta.className = "ws-card-cta";
      cta.href = ev.cta_link;
      cta.target = "_blank";
      cta.rel = "noopener noreferrer";
      cta.textContent = ev.cta_text || "Reserve";
      footer.appendChild(cta);
    }

    if (footer.children.length) body.appendChild(footer);

    card.appendChild(body);
    return card;
  }
}
